import { Activity, BellRing, CheckCircle2, Database, Server } from "lucide-react";
import { StatusBadge } from "@/components/States";
import { Button } from "@/components/ui/button";

const TAB_COMPONENTS = {
  Resumen: OverviewTab,
  Familias: FamiliesTab,
  Estudiantes: StudentsTab,
  Jobs: JobsTab,
  Planes: PlansTab,
};

export function AdminTabContent({ tab, ...props }) {
  const ActiveTab = TAB_COMPONENTS[tab];
  return ActiveTab ? <ActiveTab {...props} /> : null;
}

function OverviewTab({ overview }) {
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <OperationalAlerts alerts={overview.alerts} />

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Object.entries(overview.counts).map(([key, value]) => (
          <div key={key} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">{key.replace(/_/g, " ")}</p>
            <p className="mt-1 font-display text-2xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Server className="h-4 w-4 text-blue-400" /> Cola de jobs
          </h2>
          <p className="mt-2 text-sm text-slate-400">Profundidad de cola: {overview.jobs.queue_depth}</p>
          <ul className="mt-3 space-y-1 text-sm">
            {Object.entries(overview.jobs.by_status).map(([key, value]) => (
              <li key={key} className="flex justify-between">
                <span className="text-slate-400">{key}</span>
                <span className="font-bold">{value}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-400">
            Duracion media: {overview.jobs.avg_duration_ms ?? "—"} ms · maxima: {overview.jobs.max_duration_ms ?? "—"} ms
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Más antiguo en cola: {overview.operational.jobs.oldest_queued_seconds} s · fallos 24 h:{" "}
            {overview.operational.jobs.failures_24h} · bloqueados: {overview.operational.jobs.stale_processing}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Database className="h-4 w-4 text-emerald-400" /> Servicios
          </h2>
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Object storage</dt>
              <dd className={overview.services.storage.configured ? "text-emerald-400" : "text-rose-400"}>
                {overview.services.storage.configured ? "configurado" : "pendiente de configuracion"}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Vision</dt>
              <dd className="text-right">{overview.services.llm.vision}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Texto</dt>
              <dd className="text-right">{overview.services.llm.text}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Embeddings</dt>
              <dd className="text-right">
                {overview.services.embeddings.model.split("/").pop()} ({overview.services.embeddings.dim}d)
              </dd>
            </div>
          </dl>
          <p className="mt-4 text-xs text-slate-400">
            Actividad 24 h: {overview.activity_24h.sessions} sesiones · {overview.activity_24h.documents} paginas ·{" "}
            {overview.activity_24h.jobs} jobs
          </p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold">
            <Activity className="h-4 w-4 text-violet-400" /> Pipeline
          </h2>
          <MetricGroup label="Packs" values={overview.operational.packs} />
          <MetricGroup label="Documentos" values={overview.operational.documents} />
          <MetricGroup label="Sesiones" values={overview.operational.sessions_24h} suffix=" (24 h)" />
          <p className="mt-3 text-xs text-slate-400">
            Purgas de almacenamiento pendientes: {overview.operational.pending_storage_purges}
          </p>
          <div className="mt-3 space-y-1 text-xs text-slate-400">
            {overview.operational.jobs.durations.map((metric) => (
              <p key={metric.type} className="flex justify-between gap-3">
                <span>{metric.type}</span>
                <span>p95 {Math.round(metric.p95_ms)} ms</span>
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FamiliesTab({ families }) {
  if (!families) return null;
  return (
    <Table
      testId="admin-families"
      headers={["Familia", "Propietario", "Hijos", "Plan", "Creada"]}
      rows={families.items.map((family) => [
        family.name + (family.is_seed_data ? " (datos de desarrollo)" : ""),
        family.owner_email,
        family.students,
        family.subscription ? `${family.subscription.plan} · ${family.subscription.status}` : "—",
        new Date(family.created_at).toLocaleDateString(),
      ])}
    />
  );
}

function StudentsTab({ students }) {
  if (!students) return null;
  return (
    <Table
      testId="admin-students"
      headers={["Nombre", "Grado", "Familia", "Gaming", "Monedas", "Ultimo acceso"]}
      rows={students.items.map((student) => [
        student.first_name,
        `${student.grade}º`,
        student.family,
        student.gaming_mode ? "ON" : "OFF",
        student.coins_balance,
        student.last_login_at ? new Date(student.last_login_at).toLocaleString() : "—",
      ])}
    />
  );
}

function JobsTab({ jobs, jobFilter, onFilter, onRetry }) {
  if (!jobs) return null;
  return (
    <div data-testid="admin-jobs">
      <div className="mb-4 flex flex-wrap gap-2">
        {["", "queued", "processing", "succeeded", "failed"].map((value) => (
          <button
            key={value || "all"}
            onClick={() => onFilter(value)}
            className={`estudia-touch rounded-xl px-4 text-sm font-semibold ${
              jobFilter === value ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300"
            }`}
            data-testid={`admin-job-filter-${value || "all"}`}
          >
            {value || "todos"}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Intentos</th>
              <th className="px-4 py-3">Duracion</th>
              <th className="px-4 py-3">Error</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {jobs.items.map((job) => (
              <tr key={job.id} data-testid="admin-job-row">
                <td className="px-4 py-3 font-medium">{job.job_type}</td>
                <td className="px-4 py-3"><StatusBadge status={job.status} /></td>
                <td className="px-4 py-3">{job.attempts}/{job.max_attempts}</td>
                <td className="px-4 py-3 text-slate-400">{job.duration_ms ? `${job.duration_ms} ms` : "—"}</td>
                <td className="max-w-xs truncate px-4 py-3 text-xs text-rose-300">{job.error_message || "—"}</td>
                <td className="px-4 py-3">
                  {job.status === "failed" && (
                    <Button size="sm" onClick={() => onRetry(job.id)} data-testid={`admin-retry-job-${job.id}`}>
                      Reintentar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {jobs.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-4 text-slate-400">Sin jobs con ese filtro.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PlansTab({ subscriptions }) {
  if (!subscriptions) return null;
  return (
    <>
      <p className="mb-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
        Ningun proveedor de pago esta integrado. Los planes son entitlements internos (trial por defecto).
      </p>
      <Table
        testId="admin-subscriptions"
        headers={["Familia", "Plan", "Estado", "Trial hasta", "Proveedor de pago"]}
        rows={subscriptions.map((subscription) => [
          subscription.family,
          subscription.plan,
          subscription.status,
          subscription.trial_ends_at ? new Date(subscription.trial_ends_at).toLocaleDateString() : "—",
          subscription.payment_provider,
        ])}
      />
    </>
  );
}

function OperationalAlerts({ alerts }) {
  const items = alerts?.items || [];
  if (items.length === 0) {
    return (
      <section
        className="flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 px-5 py-4 text-emerald-100"
        data-testid="admin-alerts"
        aria-live="polite"
      >
        <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-400" aria-hidden="true" />
        <div>
          <h2 className="font-display font-bold">Operación estable</h2>
          <p className="text-sm text-emerald-200">No hay alertas operativas activas.</p>
        </div>
      </section>
    );
  }

  const critical = alerts?.status === "critical";
  return (
    <section
      className={`rounded-2xl border px-5 py-4 ${
        critical ? "border-rose-500/50 bg-rose-500/10 text-rose-100" : "border-amber-500/50 bg-amber-500/10 text-amber-100"
      }`}
      data-testid="admin-alerts"
      aria-live="polite"
    >
      <h2 className="flex items-center gap-2 font-display font-bold">
        <BellRing className={`h-5 w-5 ${critical ? "text-rose-400" : "text-amber-400"}`} aria-hidden="true" />
        {critical ? "Atención operativa urgente" : "Alertas operativas"}
      </h2>
      <ul className="mt-3 space-y-2 text-sm">
        {items.map((item) => (
          <li key={item.code} className="flex flex-wrap justify-between gap-x-4 gap-y-1 rounded-xl bg-slate-950/30 px-3 py-2">
            <span>{item.message}</span>
            <span className="font-semibold">Actual: {item.value} · Umbral: {item.threshold}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function MetricGroup({ label, values, suffix = "" }) {
  return (
    <div className="mt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}{suffix}</p>
      <p className="mt-1 text-sm text-slate-300">
        {Object.entries(values).length
          ? Object.entries(values).map(([key, value]) => `${key}: ${value}`).join(" · ")
          : "Sin actividad"}
      </p>
    </div>
  );
}

function Table({ headers, rows, testId }) {
  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-800" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-slate-950/60 text-left text-xs uppercase tracking-wide text-slate-400">
          <tr>{headers.map((header) => <th key={header} className="px-4 py-3">{header}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((row, index) => (
            <tr key={index}>
              {row.map((cell, cellIndex) => <td key={cellIndex} className="px-4 py-3">{cell}</td>)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={headers.length} className="px-4 py-4 text-slate-400">Sin registros.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
